import React, { useMemo, useState } from "react";
import { useL } from "@/lib/useL";
import { Link, useNavigate } from "react-router-dom";
import { X, Lock, ArrowRight, Sparkles } from "lucide-react";
import { CAS, CHAPTERS, pickFreeCaseIds } from "@/data/casPratiques";
import { useBilling } from "@/contexts/BillingContext";

const DISMISS_KEY = "sux1_free_cases_strip_dismissed";

/**
 * Compact "5 cas pratiques gratuits aujourd'hui" strip rendered on the
 * Dashboard above the main textarea. Only shown when:
 *   - the user is not Pro
 *   - the main textarea is currently empty (hidden mid-work)
 *   - the user has not permanently dismissed it via the "✕" button
 *
 * Clicking a card prefills the Dashboard via localStorage.sux1_reading_list_load
 * (the same channel used by the CasPratiques page) so the behaviour is
 * identical to clicking "Tester dans l'app" on the full /cas-pratiques
 * grid.
 *
 * Tri-color palette (bleu / blanc / rouge) — no black.
 */
export default function FreeCasesStrip({ textAreaEmpty = true }) {
  const L = useL();
  const navigate = useNavigate();
  const { billing, startCheckout } = useBilling();
  const isPro = !!billing?.has_paid;

  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Resolve today's 5 free case objects (daily rotation).
  const freeCases = useMemo(() => {
    const freeIds = pickFreeCaseIds();
    return CAS.filter((c) => freeIds.has(c.id));
  }, []);

  const lockedCount = CAS.length - freeCases.length;

  // Don't render for Pro users, when text is being typed, or when
  // the user already closed it.
  if (isPro) return null;
  if (!textAreaEmpty) return null;
  if (dismissed) return null;

  const chapterOf = (id) => CHAPTERS.find((ch) => ch.id === id);

  const handleCardClick = (c) => {
    // Testable cards get the sample text + format/model/audience, prompt
    // cards get the prompt itself in the textarea. Either way we route
    // through the same localStorage channel the Dashboard already reads
    // on mount.
    const payload =
      c.type === "testable"
        ? {
            kind: "text",
            text: c.sampleText || "",
            title: `Cas pratique #${c.id} — ${c.title}`,
            format: c.format,
            model: c.model,
            audience_level: c.audience_level,
          }
        : {
            kind: "text",
            text: c.prompt,
            title: `Cas pratique #${c.id} — ${c.title}`,
          };

    try {
      localStorage.setItem("sux1_reading_list_load", JSON.stringify(payload));
    } catch (_) {}

    // Soft reload of the Dashboard so the prefill effect runs again.
    navigate("/", {
      state: {
        rework: {
          text: payload.text,
          sourceTitle: payload.title,
          format: payload.format,
          model: payload.model,
          audienceLevel: payload.audience_level,
        },
      },
    });
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch (_) {}
    setDismissed(true);
  };

  const handleUpgrade = () => {
    startCheckout?.();
  };

  return (
    <section
      data-testid="free-cases-strip"
      className="border border-[#002FA7]/30 bg-[#002FA7]/5 px-4 py-4 md:px-5 md:py-5 mb-6"
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="inline-flex items-center font-mono uppercase tracking-[0.14em] text-[10px] px-2 py-1 bg-[#002FA7] text-white"
          >
            <Sparkles className="w-3 h-3 mr-1.5" />
            5 cas pratiques gratuits aujourd'hui
          </span>
          <Link
            to="/enseignants/cas-pratiques"
            data-testid="free-cases-strip-see-all"
            className="font-mono uppercase tracking-[0.14em] text-[10px] text-[#002FA7] hover:text-[#002FA7] transition-colors inline-flex items-center gap-1"
          >
            Voir les 50 cas <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="free-cases-strip-dismiss"
          className="shrink-0 text-muted-foreground hover:text-[#002FA7] transition-colors"
          aria-label="Masquer les cas pratiques"
          title="Masquer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Cards grid — 2 cols mobile, 5 cols desktop */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {freeCases.map((c) => {
          const chMeta = chapterOf(c.chapter);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => handleCardClick(c)}
              data-testid={`free-case-${c.id}`}
              className="group text-left bg-background border border-[#002FA7]/30 hover:border-[#002FA7] hover:bg-[#002FA7] hover:text-white p-3 transition-colors"
              title={c.teaser}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-mono uppercase tracking-[0.12em] text-[9px] text-[#002FA7] group-hover:text-white">
                  {chMeta?.short || "CAS"}
                </span>
                <span className="inline-flex items-center gap-1 font-mono uppercase tracking-[0.12em] text-[9px] px-1 py-0.5 bg-[#002FA7] text-white group-hover:bg-white group-hover:text-[#002FA7]">
                  Aujourd'hui
                </span>
              </div>
              <div className="text-xs font-semibold leading-snug line-clamp-2 min-h-[2.4em]">
                {c.title}
              </div>
              <div className="mt-1.5 font-mono text-[9px] text-muted-foreground group-hover:text-white/80">
                #{String(c.id).padStart(2, "0")}
              </div>
            </button>
          );
        })}
      </div>

      {/* Pro teaser */}
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <Lock className="w-3 h-3" />
          <span>
            <strong className="text-foreground">+ {lockedCount} {L("cas", "cases")}</strong>{" "}
            {L("supplémentaires avec votre premier pack de crédits", "more with your first credit pack")}
          </span>
        </div>
        <button
          type="button"
          onClick={handleUpgrade}
          data-testid="free-cases-strip-upgrade"
          className="font-mono uppercase tracking-[0.14em] text-[10px] px-3 py-1.5 bg-[#002FA7] text-white hover:bg-[#002FA7] transition-colors inline-flex items-center gap-1.5"
        >
          {L("Débloquer, dès 2 €", "Unlock, from €2")}
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </section>
  );
}
